/** External **/
import React from "react";
import moment from "moment";
import _ from "lodash";

/** Design System **/
import { ClockOutlined, TickCircleOutlined } from "@toddle-edu/ds-icons";

/** Internal **/
import {
  CURRICULUM_TYPE_PYP,
  CURRICULUM_VARIANT_TODDLE_PLAY,
} from "Constants/stringConstants";
import { formatDate } from "Utils/DateTimeHelpers";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import {
  PERFORMANCE_ANALYTICS,
  TODDLE_ANALYTICS_MODULES,
  ATTENDANCE_SETTINGS_V2_CONFIG_ID,
} from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnContants";
import {
  FILTER_CURRICULUM_PROGRAM,
  FILTER_ORGANIZATION,
} from "ToddleAnalytics/modules/Constants";
import { getLastSyncTimeFromCache } from "ToddleAnalytics/modules/ToddleAnalyticsGraphqlHelpers";

export const getTooltipText = ({
  t,
  moduleName,
  acronym,
  curriculumProgramType,
  curriculumProgramVariant,
}) => {
  switch (moduleName) {
    case PERFORMANCE_ANALYTICS: {
      if (_.isEqual(curriculumProgramType, CURRICULUM_TYPE_PYP)) {
        return t(
          "toddleAnalytics:performance_insights_not_available_with_label",
          {
            label: acronym,
          }
        );
      }
      if (_.isEqual(curriculumProgramVariant, CURRICULUM_VARIANT_TODDLE_PLAY)) {
        return t(
          "toddleAnalytics:performance_insights_not_available_with_label",
          {
            label: acronym,
          }
        );
      }
    }
  }
};

export const getMutationInput = ({
  previousPermissions,
  currentPermissions,
  curriculumPrograms,
}) => {
  const permissions = [];
  let isAtleastOneModuleEnabled = false;

  _.forEach(curriculumPrograms, curriculumProgram => {
    const curriculumProgramId = _.get(curriculumProgram, "id");
    const enabledModules = [];
    const disabledModules = [];

    _.forEach(TODDLE_ANALYTICS_MODULES, module => {
      const moduleName = _.get(module, "id");
      const mappingId = `${curriculumProgramId}-${moduleName}`;
      if (
        !_.isEqual(
          previousPermissions[mappingId],
          currentPermissions[mappingId]
        )
      ) {
        if (currentPermissions[mappingId]) {
          enabledModules.push(moduleName);
        } else {
          disabledModules.push(moduleName);
        }
      }

      isAtleastOneModuleEnabled =
        isAtleastOneModuleEnabled || currentPermissions[mappingId];
    });
    permissions.push({
      curriculumProgramId,
      enabledModules,
      disabledModules,
    });
  });
  return {
    disableParentPerm: isAtleastOneModuleEnabled ? undefined : true,
    permissions,
  };
};

export const getLastSyncTimeQueryVariables = ({ moduleName, orgId }) => {
  return {
    id: orgId,
    lastSyncTimeFilterInput: {
      organizationId: orgId,
      moduleType: moduleName,
    },
  };
};

export const getAllModulesLastSyncQueryVariables = ({ modules, orgId }) => {
  const allQueryVariables = {};

  _.forEach(modules, module => {
    const moduleName = _.get(module, "id");

    const queryVariablesKey = _.get(module, "lastSyncQueryVariablesKey");

    allQueryVariables[queryVariablesKey] = getLastSyncTimeQueryVariables({
      moduleName,
      orgId,
    });
  });

  return allQueryVariables;
};

export const getLastSyncQueryDetails = ({ moduleName, orgId }) => {
  const queryVariables = getLastSyncTimeQueryVariables({ moduleName, orgId });
  const queryData = getLastSyncTimeFromCache(queryVariables);
  return _.get(queryData, "analytics.lastSyncTime", []);
};

export const getLastSyncTimeProps = ({
  t,
  orgId,
  moduleName,
  curriculumProgramId,
}) => {
  const lastSyncQueryDetails = getLastSyncQueryDetails({ moduleName, orgId });

  let lastSyncObject = _.find(lastSyncQueryDetails, {
    entityType: FILTER_ORGANIZATION,
  });

  if (_.isEmpty(lastSyncObject)) {
    lastSyncObject = _.find(
      lastSyncQueryDetails,
      ({ entityType, entityId }) =>
        _.isEqual(entityType, FILTER_CURRICULUM_PROGRAM) &&
        _.isEqual(entityId, curriculumProgramId)
    );
  }

  const lastSyncTimeStamp = _.get(lastSyncObject, "timestamp");

  if (!lastSyncTimeStamp) {
    return {
      icon: <TickCircleOutlined variant="warning" size="xxx-small" />,
      text: t("toddleAnalytics:sync_in_progress"),
      textColor: "var(--text-semantic-warning)",
    };
  }

  const lastSyncedDateWithTime = formatDate({
    date: moment.utc(lastSyncTimeStamp),
    format: DATE_TIME_CONSTANTS.D_MMM_YY_h_mm_A,
  });

  return {
    icon: <ClockOutlined variant="link" size="xxx-small" />,
    text: t("toddleAnalytics:last_synced_with_date_and_time", {
      date_and_time: lastSyncedDateWithTime,
    }),
    textColor: "var(--text-secondary)",
  };
};

export const isTimezoneConfigured = ({ organizationDetails }) => {
  const timezoneDetails = _.find(organizationDetails, detail => {
    return _.isEqual(_.get(detail, "name"), "TIMEZONE");
  });

  return !_.isEmpty(_.get(timezoneDetails, "value"));
};

export const getConfigurationQueryFilters = ({ orgId }) => {
  const configQueryFilters = {
    configurationIds: [ATTENDANCE_SETTINGS_V2_CONFIG_ID],
    searchText: orgId,
  };
  return configQueryFilters;
};
